import { useState } from "react";
import EditorPreviewContainer from "../EditorPreviewContainer";
import { FlyerForm } from "../FlyerEditor";
import FlyerPreview from "../FlyerPreview";
import useLayout from "../util/useLayout";
import { useTranslation } from "react-i18next";
import { useFlyerConfig } from "../util/useFlyerConfig";
import { Spacer } from "../editor/EditorHelpers";
import { BackgroundProps } from "../components/BackgroundPicker";
import { BrochurePreviewFront } from "./brochure/BrochurePreviewFront";
import { BrochurePreviewBack } from "./brochure/BrochurePreviewBack";
import AlternativesEditor, { AlternativesVP } from "../editor/AlternativesEditor";
import { useRestorable, v } from "../util/useRestorable";
import { PresetTextEditor } from "../components/TextEditor";
import { PresetMultilineTextEditor } from "../components/MultilineTextEditor";


export default function Brochure() {
  const { t } = useTranslation();
  const { flyerLocale } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { isSmallScreen } = useLayout();

  const [bigHeader, setBigHeader] = useRestorable(dt('default.buy-european.title').toUpperCase(), 'bigHeader');
  const [subheader, setSubheader] = useRestorable(dt('default.invest-in-quality-invest-in-europe.title').toUpperCase(), 'subheader');
  const [page1Text, setPage1Text] = useRestorable(dt('default.brochure.page1.text'), 'page1Text');
  const [page2TextTop, setPage2TextTop] = useRestorable(dt('default.brochure.page2.top.text'), 'page2TextTop');
  const [page2TextBottom, setPage2TextBottom] = useRestorable(dt('default.brochure.page2.bottom.text'), 'page2TextBottom');
  const [page2TextTopCircle, setPage2TextTopCircle] = useRestorable(dt('default.brochure.page2.top.circle.text').toUpperCase(), 'page2TextTopCircle');
  const [page2TextBottomCircle, setPage2TextBottomCircle] = useRestorable(dt('default.brochure.page2.bottom.circle.text').toUpperCase(), 'page2TextBottomCircle');
  const [page3TextTop, setPage3TextTop] = useRestorable(dt('default.brochure.page3.top.text'), 'page3TextTop');
  const [page3TextCircle, setPage3TextCircle] = useRestorable(dt('default.brochure.page3.circle.text'), 'page3TextCircle');
  const [page3TextBottom, setPage3TextBottom] = useRestorable(dt('default.brochure.page3.bottom.text'), 'page3TextBottom');
  const [alternatives, setAlternatives] = useState<null | AlternativesVP>(null);
  const [page4TextTop, setPage4TextTop] = useRestorable(dt('default.brochure.page4.top.text'), 'page4TextTop');
  const [page6TextBottom, setPage6TextBottom] = useRestorable(dt('default.brochure.page6.bottom.text'), 'page6TextBottom');
  const [europeanOwned, setEuropeanOwned] = useRestorable(dt('default.alternative.title'), 'europeanOwned');
  const [notEuropeanOwned, setNotEuropeanOwned] = useRestorable(dt('default.not-european.title'), 'notEuropeanOwned');

  const background: BackgroundProps = {
    backgroundColor: "#FFFFFF",
  };

  return (
    <EditorPreviewContainer>
      <FlyerForm $isSmallScreen={isSmallScreen}>
        <PresetTextEditor 
          label={t('big-header.label')}
          value={bigHeader}
          onChange={setBigHeader}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('subheader.label')}
          value={subheader}
          onChange={setSubheader}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page1-text.label')}
          value={page1Text}
          rows={3}
          onChange={setPage1Text}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page2-text-top.label')}
          value={page2TextTop}
          rows={7}
          onChange={setPage2TextTop}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('page2-text-top-circle.label')}
          value={page2TextTopCircle}
          onChange={setPage2TextTopCircle}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page2-text-bottom.label')}
          value={page2TextBottom}
          rows={9}
          onChange={setPage2TextBottom}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('page2-text-bottom-circle.label')}
          value={page2TextBottomCircle}
          onChange={setPage2TextBottomCircle}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page3-text-top.label')}
          value={page3TextTop}
          rows={8}
          onChange={setPage3TextTop}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page3-text-circle.label')}
          value={page3TextCircle}
          rows={2}
          onChange={setPage3TextCircle}
        />
        <Spacer $height="15px"/>
        <PresetMultilineTextEditor 
          label={t('page3-text-bottom.label')}
          value={page3TextBottom}
          rows={3}
          onChange={setPage3TextBottom}
        />
        <Spacer $height="15px"/>
        <AlternativesEditor
          onChange={(update)  => setAlternatives(update)}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('not-european-owned.label')}
          value={notEuropeanOwned}
          onChange={setNotEuropeanOwned}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('european-owned.label')}
          value={europeanOwned}
          onChange={setEuropeanOwned}
        />
        <Spacer $height="15px"/>
        {true && <PresetMultilineTextEditor 
          label={t('page4-text-top.label')}
          value={page4TextTop}
          rows={2}
          onChange={setPage4TextTop}
        />}
        {true && <Spacer $height="15px"/>}
        <PresetMultilineTextEditor 
          label={t('page6-text-bottom.label')}
          value={page6TextBottom}
          rows={2}
          onChange={setPage6TextBottom}
        />
      </FlyerForm>
      <FlyerPreview 
        background={background} 
        getAnalyticsDetails={() => JSON.stringify({
          bigHeader: v(bigHeader),
          subheader: v(subheader),
          page1Text: v(page1Text),
          page2TextTop: v(page2TextTop),
          page2TextTopCircle: v(page2TextTopCircle),
          page2TextBottom: v(page2TextBottom),
          page2TextBottomCircle: v(page2TextBottomCircle),
          page3TextTop: v(page3TextTop),
          page3TextCircle: v(page3TextCircle),
          page3TextBottom: v(page3TextBottom),
          alternatives: alternatives,
          page4TextTop: v(page4TextTop),
          page6TextBottom: v(page6TextBottom),
          notEuropeanOwned: v(notEuropeanOwned),
          europeanOwned: v(europeanOwned),
        })} >
        <BrochurePreviewFront
          bigHeader={v(bigHeader)}
          subheader={v(subheader)}
          page1Text={v(page1Text)}
          page2TextTop={v(page2TextTop)}
          page2TextTopCircle={v(page2TextTopCircle)}
          page2TextBottom={v(page2TextBottom)}
          page2TextBottomCircle={v(page2TextBottomCircle)}
          page3TextTop={v(page3TextTop)}
          page3TextCircle={v(page3TextCircle)}
          page3TextBottom={v(page3TextBottom)}
        />
        <BrochurePreviewBack
          alternatives={alternatives}
          page4TextTop={v(page4TextTop)}
          page6TextBottom={v(page6TextBottom)}
          notEuropeanOwned={v(notEuropeanOwned)}
          europeanOwned={v(europeanOwned)}
        />
      </FlyerPreview>
    </EditorPreviewContainer>
  )
}
